import {PERSONALIZATION_FONT_ORDER, type PersonalizationFontKey} from "./personalizationFont";

/**
 * Fetches a chosen display face, and nothing else (ui#1814, owner constraint 2026-09-01).
 *
 * Plain TypeScript — no React import. The React seam is whichever surface needs the face on screen:
 * the recipient's download page for the sender's ONE choice, and the builder's picker for its
 * specimens.
 *
 * **Why a dynamic import rather than `src/theme/fonts.ts`.** That module is imported once from
 * `main.tsx`, so everything in it is declared on every page — five more families there would put five
 * more sets of `@font-face` rules into the render-blocking stylesheet of every content route, which is
 * exactly the regression ui#1795 measured and fixed (22,983 → 3,133 bytes gzipped). Each face here
 * lands in its own lazily-fetched CSS chunk instead, so a page that never chose one pays nothing.
 *
 * **Why the per-WEIGHT entrypoints.** They are the repo's standing rule (`src/theme/fonts.ts`): a
 * per-weight file declares each SUBSET with its own `unicode-range`, so the browser fetches the `latin`
 * woff2 for an English page and additionally the `latin-ext` one for a Romanian page carrying ă â î ș ț.
 * A per-subset entrypoint would force both on every visitor. Two weights per face — 400 for the copy,
 * 700 for the sender's name — which is the owner's "one or two weights" stated as code in
 * {@link PERSONALIZATION_FONT_WEIGHTS} and honoured by this table.
 */

/**
 * The face's stylesheets, keyed by the stored choice. A total `Record`, so adding a face to the union
 * without giving it something to fetch is a compile error rather than a specimen that never loads.
 *
 * The specifiers are LITERAL on purpose: Vite resolves and fingerprints a dynamic import at build time
 * only when it can see the module id, and a computed `@fontsource/${slug}/400.css` would resolve to
 * nothing in the built bundle while working perfectly in dev.
 */
const FONT_STYLESHEETS: Record<PersonalizationFontKey, () => Promise<unknown>> = {
	FRAUNCES: () => Promise.all([
		import("@fontsource/fraunces/400.css"),
		import("@fontsource/fraunces/700.css"),
	]),
	OUTFIT: () => Promise.all([
		import("@fontsource/outfit/400.css"),
		import("@fontsource/outfit/700.css"),
	]),
	ALEGREYA_SANS: () => Promise.all([
		import("@fontsource/alegreya-sans/400.css"),
		import("@fontsource/alegreya-sans/700.css"),
	]),
	BRICOLAGE_GROTESQUE: () => Promise.all([
		import("@fontsource/bricolage-grotesque/400.css"),
		import("@fontsource/bricolage-grotesque/700.css"),
	]),
	ZILLA_SLAB: () => Promise.all([
		import("@fontsource/zilla-slab/400.css"),
		import("@fontsource/zilla-slab/700.css"),
	]),
};

/** Faces already asked for, so a re-render or a second surface does not re-enter the import. */
const requested = new Set<PersonalizationFontKey>();

/**
 * Declares one face's `@font-face` rules in this document.
 *
 * Resolves to `true` when the face is (or already was) declared, and `false` when there is nothing to
 * do — no browser, or a stored choice this build cannot paint. A failed chunk fetch resolves `false`
 * rather than throwing: the page then renders in the standard face, which is what
 * {@link ./personalizationFont.recipientFontStack} already falls back to, and a rejected promise here
 * would turn a cosmetic miss into an unhandled error on a recipient's page.
 */
export async function loadPersonalizationFont(
	font: PersonalizationFontKey | null | undefined,
): Promise<boolean> {
	// `window` is the honest browser question (ui#618). During the SSG prerender there is no document to
	// declare anything in, and the recipient page's font choice is client-fetched anyway.
	if (font == null || typeof window === "undefined" || !(font in FONT_STYLESHEETS)) {
		return false;
	}
	if (requested.has(font)) {
		return true;
	}
	requested.add(font);
	try {
		await FONT_STYLESHEETS[font]();
		return true;
	} catch {
		// Allow a later attempt: a chunk that failed on a flaky connection may well load next time, and
		// the page is readable in the standard face until it does.
		requested.delete(font);
		return false;
	}
}

/**
 * Declares EVERY offered face — what the builder's picker needs, because each specimen has to be drawn
 * in the face it is offering.
 *
 * Deliberately a separate entry point from {@link loadPersonalizationFont}, and deliberately not
 * reachable from any recipient surface: the whole owner constraint is that a download page fetches the
 * ONE face its sender chose. The builder is a signed-in sender opening a sheet on purpose, so paying
 * for five small CSS chunks there costs no content-route LCP at all.
 */
export async function loadPersonalizationFontSpecimens(
	fonts: readonly PersonalizationFontKey[] = PERSONALIZATION_FONT_ORDER,
): Promise<void> {
	await Promise.all(fonts.map(font => loadPersonalizationFont(font)));
}
